import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listFeatureFlags,
  createFeatureFlag,
  updateFeatureFlag,
  deleteFeatureFlag,
} from '../../api/superAdmin';
import type { FeatureFlag } from '../../api/superAdmin';
import styles from './FeatureFlagsPanel.module.css';
import adminStyles from './AdminPage.module.css';

const TIERS = ['free', 'professional', 'enterprise'] as const;

export function FeatureFlagsPanel() {
  const { t } = useTranslation(['admin', 'common']);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_enabled: false,
    tier_overrides: {} as Record<string, boolean>,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadFlags();
  }, []);

  async function loadFlags() {
    try {
      setLoading(true);
      const data = await listFeatureFlags();
      setFlags(data);
    } catch {
      // handle
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) return;
    try {
      await createFeatureFlag({
        name: formData.name.trim(),
        description: formData.description || null,
        default_enabled: formData.default_enabled,
        tier_overrides: formData.tier_overrides,
      });
      setShowForm(false);
      setFormData({ name: '', description: '', default_enabled: false, tier_overrides: {} });
      await loadFlags();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || t('admin:ffCreateFailed'));
    }
  }

  async function handleToggleTier(flag: FeatureFlag, tier: string) {
    const current = flag.tier_overrides[tier] ?? flag.default_enabled;
    const newOverrides = { ...flag.tier_overrides, [tier]: !current };
    setSaving(flag.name);
    try {
      await updateFeatureFlag(flag.name, { tier_overrides: newOverrides });
      await loadFlags();
    } catch {
      // handle
    } finally {
      setSaving(null);
    }
  }

  async function handleToggleDefault(flag: FeatureFlag) {
    setSaving(flag.name);
    try {
      await updateFeatureFlag(flag.name, { default_enabled: !flag.default_enabled });
      await loadFlags();
    } catch {
      // handle
    } finally {
      setSaving(null);
    }
  }

  async function handleDelete(name: string) {
    if (!confirm(t('admin:ffDeleteConfirm'))) return;
    try {
      await deleteFeatureFlag(name);
      await loadFlags();
    } catch {
      // handle
    }
  }

  if (loading) return <div className={adminStyles.empty}>{t('admin:ffLoading')}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className={adminStyles.createBtn} onClick={() => setShowForm(!showForm)}>
          {t('admin:ffAdd')}
        </button>
      </div>

      {showForm && (
        <form className={adminStyles.form} onSubmit={handleCreate}>
          {error && (
            <div style={{ color: 'var(--color-danger)', fontSize: 13, marginBottom: 8 }}>
              {error}
            </div>
          )}
          <div className={adminStyles.formRow}>
            <input
              className={adminStyles.input}
              placeholder={t('admin:ffName')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <input
              className={adminStyles.input}
              placeholder={t('admin:ffDescription')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className={styles.checkboxRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.default_enabled}
                onChange={(e) => setFormData({ ...formData, default_enabled: e.target.checked })}
              />
              {t('admin:ffDefaultEnabled')}
            </label>
            {TIERS.map((tier) => (
              <label key={tier} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.tier_overrides[tier] ?? formData.default_enabled}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tier_overrides: { ...formData.tier_overrides, [tier]: e.target.checked },
                    })
                  }
                />
                {tier}
              </label>
            ))}
          </div>
          <div className={adminStyles.formActions}>
            <button type="button" className={adminStyles.actionBtn} onClick={() => setShowForm(false)}>
              {t('common:cancel')}
            </button>
            <button type="submit" className={adminStyles.createBtn}>{t('common:create')}</button>
          </div>
        </form>
      )}

      {flags.length === 0 ? (
        <div className={adminStyles.empty}>{t('admin:ffEmpty')}</div>
      ) : (
        <table className={adminStyles.table}>
          <thead>
            <tr>
              <th>{t('admin:ffName')}</th>
              <th>{t('admin:ffDescription')}</th>
              <th>{t('admin:ffDefault')}</th>
              {TIERS.map((tier) => (
                <th key={tier} style={{ textTransform: 'capitalize' }}>{tier}</th>
              ))}
              <th>{t('common:actions')}</th>
            </tr>
          </thead>
          <tbody>
            {flags.map((flag) => (
              <tr key={flag.name} style={{ opacity: saving === flag.name ? 0.6 : 1 }}>
                <td><code className={styles.flagName}>{flag.name}</code></td>
                <td className={styles.descCell}>{flag.description || '—'}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={flag.default_enabled}
                    onChange={() => handleToggleDefault(flag)}
                    disabled={saving === flag.name}
                  />
                </td>
                {TIERS.map((tier) => (
                  <td key={tier}>
                    <input
                      type="checkbox"
                      checked={flag.tier_overrides[tier] ?? flag.default_enabled}
                      onChange={() => handleToggleTier(flag, tier)}
                      disabled={saving === flag.name}
                    />
                  </td>
                ))}
                <td>
                  <button
                    className={`${adminStyles.actionBtn} ${adminStyles.dangerBtn}`}
                    onClick={() => handleDelete(flag.name)}
                  >
                    {t('common:delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
